import {
  ADMIN_DELIVERIES_DETAILS,
  ADMIN_ORDER_DETAILS,
  Box,
  DebtRepayment,
  Disclaimer,
  Div,
  H5,
  P,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { formatDayTime } from '../helpers/receivablesLabels';
import { repaymentSourceLabel } from '../helpers/repaymentPlanLabels';

/** Widths shared with the collections' TableHeader, so the columns line up. */
export const REPAYMENT_COLUMNS = [
  { label: 'Date', width: '25%' },
  { label: 'Collected from', width: '45%' },
  { label: 'Plan', width: '15%' },
  { label: 'Amount', width: '15%' },
];

/**
 * Where the order or the delivery behind a collection opens, in a new tab so
 * the receivables page stays put. The blue card has nothing to open: it is a
 * Balance Correction on the owner's transactions.
 */
const linkFor = (repayment: DebtRepayment) => {
  if (repayment.source === 'virtualBrandOrder' && repayment.orderId != null)
    return ADMIN_ORDER_DETAILS.replace(':id', String(repayment.orderId));
  if (repayment.source === 'vDriveDelivery' && repayment.orderVDriveId != null)
    return ADMIN_DELIVERIES_DETAILS.replace(
      ':id',
      String(repayment.orderVDriveId)
    );
  return null;
};

/** One amount a repayment plan collected. */
export const DebtRepaymentRow = ({
  repayment,
}: {
  repayment: DebtRepayment;
}) => {
  const source = <RepaymentSource repayment={repayment} />;
  const amount = (
    <H5 $m="0" $color={lightTheme.green}>
      {displayCentsInDollars(repayment.amount)}
    </H5>
  );

  return (
    // One line of text: Box's 70px minimum left it twice as tall as needed.
    <Box $mb="8" $minHeight="auto" $minHeightSM="auto">
      <Div $desktop $flex $alignCenter $gap="24px">
        <Div $width={REPAYMENT_COLUMNS[0].width}>
          <P $m="0">{formatDayTime(repayment.createdAt)}</P>
        </Div>
        <Div $width={REPAYMENT_COLUMNS[1].width}>{source}</Div>
        <Div $width={REPAYMENT_COLUMNS[2].width}>
          <P $m="0">#{repayment.planId}</P>
        </Div>
        <Div $width={REPAYMENT_COLUMNS[3].width}>{amount}</Div>
      </Div>

      {/* A phone card: when and from what on the left, how much and on which
          plan on the right. A "label: value" line per column made every
          collection a four-line card. */}
      <Div $mobile $flex $alignCenter $between $gap="12px">
        <Div $flex $column $gap="4px" $minWidth="0">
          <P $m="0">{formatDayTime(repayment.createdAt)}</P>
          {source}
        </Div>
        <Div $flex $column $alignEnd $gap="4px" $flexNone>
          {amount}
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            Plan #{repayment.planId}
          </Disclaimer>
        </Div>
      </Div>
    </Box>
  );
};

/** The order or delivery as a link; the blue card as plain text. */
const RepaymentSource = ({ repayment }: { repayment: DebtRepayment }) => {
  const href = linkFor(repayment);
  const label = repaymentSourceLabel(repayment);
  if (!href) return <P $m="0">{label}</P>;
  return (
    <P
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      $m="0"
      $pointer
      style={{ color: lightTheme.primary, textDecoration: 'underline' }}
    >
      {label}
    </P>
  );
};
